import { useEffect, useState } from 'react';
import { ApiError } from '../api/client';
import { NETWORK_ERROR_MESSAGE, documentReadFailureMessage } from '../api/messages';
import type { RoomDocument } from '../api/types';

/**
 * WHAT THE DATA-ROOM VIEWER SHOWS (owner, 2026-10-15): "Everyone who is viewing
 * the file in the data room should see the same redactions. It just makes for
 * confusion otherwise." So the admin's viewer shows what BUYERS are served
 * (ADR-0064) — never a choice between copies:
 *
 *   - redaction `complete` → the verified redacted copy, read from
 *     GET /api/v1/rooms/:roomId/documents/:documentId/redaction/rendition
 *     (room admin and bound seller only; 200 with the copy's bytes — the same
 *     kind of file as the upload, ADR-0067: a PDF, a picture, a TIFF, a Word /
 *     Excel file).
 *     A 404 there means the run ended `not_needed` (nothing needed hiding), so
 *     buyers get the original — and so does this viewer;
 *   - redaction `off`, deal paperwork (NDA, LOI, signing documents — never
 *     redacted), or a server that does not report the state → the original;
 *   - pending / not started (null) → "Being prepared", exactly as buyers see it;
 *   - needs_review / unsupported / failed → "Being prepared" with the reason,
 *     plus a way into the redaction review for people who can act on it;
 *   - an unpacked zip / saved email (`archive`, reported to the room admin and
 *     the seller only) → the container itself, with a note: buyers never see it,
 *     only the files that came out of it, each with its own redaction (review F2);
 *   - a zip / saved email NOT unpacked (yet, or refused: damaged, locked, too big)
 *     → a plain statement of that, never "Being prepared" — no redaction check
 *     ever runs on a container, only on the files that come out of it.
 *
 * No API route or authorisation changes: this only picks which existing
 * endpoint the viewer loads.
 */

/** Why there is nothing to show yet. */
export interface ViewerNotice {
  title: string;
  detail: string;
  /** `check`: a person should act; `muted`: in progress. */
  tone: 'muted' | 'check';
  /** The redaction needs a person — offer "Review redaction" where available. */
  reviewable: boolean;
}

/** What buyers see while there is no copy for them (ADR-0064), and until when. */
const beingPrepared = (until: string) => `Buyers see “Being prepared” for this document ${until}.`;

export type ViewerPlan =
  /** Buyers get the original: show it. */
  | { kind: 'original' }
  /** An unpacked zip / saved email: buyers never see it — its files are listed
   * separately. The admin and the seller may open the container itself. */
  | { kind: 'container'; note: string }
  /** Redaction complete: show the redacted copy (the original when none was needed). */
  | { kind: 'redacted' }
  /** Buyers get nothing yet: say why. */
  | { kind: 'held'; notice: ViewerNotice };

/** What the viewer says about an unpacked zip / saved email (review F2). */
export const CONTAINER_NOTE =
  'Buyers never see the container itself — its files are listed separately.';

/** What the viewer says about a zip / saved email whose files are not in the room yet. */
export const CONTAINER_PENDING_NOTE = 'Its files are added to the room one by one.';

/** "zip" / "eml" / "msg" for a file the server opens into its files (ingestion/containers.ts), else null. */
function containerExtensionOf(name: string | undefined): 'zip' | 'eml' | 'msg' | null {
  const match = /.(zip|eml|msg)$/i.exec(name?.trim() ?? '');
  return match ? (match[1]!.toLowerCase() as 'zip' | 'eml' | 'msg') : null;
}

/** Which copy the data-room viewer shows for a document — the one buyers get. */
export function viewerPlan(
  doc: Pick<RoomDocument, 'redactionStatus' | 'paperworkKind'> &
    Partial<Pick<RoomDocument, 'archive' | 'name' | 'unreadable' | 'fromArchive'>>,
): ViewerPlan {
  // A container is never redacted (its files are): no check ever runs on it.
  if (doc.archive) return { kind: 'container', note: CONTAINER_NOTE };
  const containerExtension = doc.fromArchive ? null : containerExtensionOf(doc.name);
  if (containerExtension) {
    return {
      kind: 'held',
      notice: {
        title: `This is a .${containerExtension} file`,
        // Refused: the plain reason (a damaged, locked or too large file), which asks
        // for a readable copy. Otherwise it is on its way to being opened.
        detail: doc.unreadable?.message ?? CONTAINER_PENDING_NOTE,
        tone: doc.unreadable ? 'check' : 'muted',
        reviewable: false,
      },
    };
  }
  const status = doc.redactionStatus;
  // Not reported (mocks, older servers), redaction off, or deal paperwork.
  if (status === undefined || status === 'off' || doc.paperworkKind) return { kind: 'original' };
  switch (status) {
    case 'complete':
      return { kind: 'redacted' };
    case 'pending':
      return {
        kind: 'held',
        notice: {
          title: 'Being prepared',
          detail: `The redaction check is still running. ${beingPrepared('until it finishes')}`,
          tone: 'muted',
          reviewable: false,
        },
      };
    case 'needs_review':
      return {
        kind: 'held',
        notice: {
          title: 'Being prepared — redaction needs review',
          detail: `Some redactions are uncertain, or the copy could not be made safely. ${beingPrepared('until a person reviews it')}`,
          tone: 'check',
          reviewable: true,
        },
      };
    case 'unsupported':
      return {
        kind: 'held',
        notice: {
          title: 'Being prepared — redaction needs review',
          detail: `This file type can’t be redacted automatically. ${beingPrepared('until a person reviews it')}`,
          tone: 'check',
          reviewable: true,
        },
      };
    case 'failed':
      return {
        kind: 'held',
        notice: {
          title: 'Being prepared — redaction needs review',
          detail: `The redaction check could not finish. ${beingPrepared('until a person resolves it')}`,
          tone: 'check',
          reviewable: true,
        },
      };
    default:
      // null: not filed yet, or no redaction check has started.
      return {
        kind: 'held',
        notice: {
          title: 'Being prepared',
          detail: `The redaction check has not run yet. ${beingPrepared('until it has run')}`,
          tone: 'muted',
          reviewable: false,
        },
      };
  }
}

export type RedactedCopyState =
  | { kind: 'idle' }
  | { kind: 'loading' }
  /** No redacted copy exists (nothing needed hiding): buyers get the original. */
  | { kind: 'none' }
  | { kind: 'pdf'; url: string; data: ArrayBuffer }
  /** A redacted picture (PNG / JPEG / WebP — the upload's own kind, ADR-0067): shown as is. */
  | { kind: 'image'; url: string; extension: string }
  /** A redacted Word / Excel file or TIFF: shown through the server's preview of it, or downloaded. */
  | { kind: 'file'; url: string; extension: string }
  | { kind: 'error'; message: string };

export function renditionPath(roomId: string, documentId: string): string {
  return `/api/v1/rooms/${roomId}/documents/${documentId}/redaction/rendition`;
}

/** Copies a browser shows as a picture. */
const IMAGE_EXTENSIONS: Readonly<Record<string, string>> = {
  'image/png': 'png',
  'image/jpeg': 'jpg',
  'image/webp': 'webp',
};

function extensionFor(contentType: string): string {
  const ct = contentType.split(';')[0]?.trim().toLowerCase() ?? '';
  if (ct === 'application/pdf') return 'pdf';
  if (IMAGE_EXTENSIONS[ct]) return IMAGE_EXTENSIONS[ct];
  if (ct === 'image/tiff') return 'tif';
  if (ct.includes('presentationml')) return 'pptx';
  if (ct === 'text/csv') return 'csv';
  if (ct.includes('spreadsheet') || ct.includes('excel')) return 'xlsx';
  return 'docx';
}

/**
 * Load the redacted copy while `active` (the plan is `redacted`). Re-runs when
 * the document changes or the plan flips (the listing polls, so a copy that
 * finishes while the viewer is open appears). The object URL is revoked when
 * the copy is replaced or the viewer closes.
 */
export function useRedactedCopy(
  roomId: string,
  documentId: string,
  active: boolean,
): RedactedCopyState {
  const [state, setState] = useState<RedactedCopyState>(
    active ? { kind: 'loading' } : { kind: 'idle' },
  );

  useEffect(() => {
    if (!active) {
      setState({ kind: 'idle' });
      return;
    }
    let objectUrl: string | null = null;
    const controller = new AbortController();
    setState({ kind: 'loading' });
    fetch(renditionPath(roomId, documentId), {
      credentials: 'include',
      signal: controller.signal,
    })
      .then(async (res) => {
        if (res.status === 404) {
          if (!controller.signal.aborted) setState({ kind: 'none' });
          return;
        }
        if (!res.ok) {
          throw new ApiError(
            res.status,
            documentReadFailureMessage(res.status, 'This document is not available to view.'),
          );
        }
        const blob = await res.blob();
        const extension = extensionFor(res.headers.get('Content-Type') ?? blob.type);
        const data = extension === 'pdf' ? await blob.arrayBuffer() : null;
        if (controller.signal.aborted) return;
        objectUrl = URL.createObjectURL(blob);
        setState(
          data
            ? { kind: 'pdf', url: objectUrl, data }
            : Object.values(IMAGE_EXTENSIONS).includes(extension)
              ? { kind: 'image', url: objectUrl, extension }
              : { kind: 'file', url: objectUrl, extension },
        );
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        setState({
          kind: 'error',
          message: err instanceof ApiError ? err.message : NETWORK_ERROR_MESSAGE,
        });
      });
    return () => {
      controller.abort();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [roomId, documentId, active]);

  return state;
}

/** "Lease.pdf" → "Lease (redacted).pdf", with the copy's own extension. */
export function redactedFilename(name: string, extension: string): string {
  const dot = name.lastIndexOf('.');
  const base = dot > 0 ? name.slice(0, dot) : name;
  return `${base} (redacted).${extension}`;
}
